const DB_NAME = 'emberDraftDrafts';
const STORE_NAME = 'photos';

function isIndexedDBAvailable() {
  return typeof indexedDB !== 'undefined' && indexedDB !== null;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!isIndexedDBAvailable()) {
      reject(new Error('IndexedDB is not available'));
      return;
    }

    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error ?? new Error('Failed to open IndexedDB'));
    };
  });
}

export async function saveDraftPhoto(key: string, file: File): Promise<void> {
  try {
    const buffer = await file.arrayBuffer();
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put(
        {
          buffer,
          type: file.type,
          name: file.name,
          lastModified: file.lastModified,
        },
        key
      );
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        const err = tx.error ?? new Error('Failed to save draft photo');
        db.close();
        reject(err);
      };
      tx.onabort = () => {
        const err = tx.error ?? new Error('Draft photo transaction aborted');
        db.close();
        reject(err);
      };
    });
  } catch (error) {
    console.warn('saveDraftPhoto failed', error);
  }
}

export async function loadDraftPhoto(key: string): Promise<File | null> {
  try {
    const db = await openDatabase();
    return await new Promise<File | null>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => {
        const payload = request.result;
        if (!payload) {
          resolve(null);
          return;
        }
        try {
          const blob = new Blob([payload.buffer], { type: payload.type || 'image/jpeg' });
          const file = new File([blob], payload.name || 'photo.jpg', {
            type: payload.type || 'image/jpeg',
            lastModified: payload.lastModified || Date.now(),
          });
          resolve(file);
        } catch (err) {
          console.warn('Failed to reconstruct draft photo', err);
          resolve(null);
        }
      };

      request.onerror = () => {
        reject(request.error ?? new Error('Failed to load draft photo'));
      };

      tx.oncomplete = () => {
        db.close();
      };

      tx.onerror = () => {
        const err = tx.error ?? new Error('Failed to read draft photo');
        db.close();
        reject(err);
      };
    });
  } catch (error) {
    console.warn('loadDraftPhoto failed', error);
    return null;
  }
}

export async function clearDraftPhoto(key: string): Promise<void> {
  try {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.delete(key);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        const err = tx.error ?? new Error('Failed to clear draft photo');
        db.close();
        reject(err);
      };
      tx.onabort = () => {
        const err = tx.error ?? new Error('Draft photo transaction aborted');
        db.close();
        reject(err);
      };
    });
  } catch (error) {
    console.warn('clearDraftPhoto failed', error);
  }
}

export function dataUrlToFile(dataUrl: string, fileName: string, mimeType?: string): File | null {
  try {
    const parts = dataUrl.split(',');
    if (parts.length < 2) {
      return null;
    }
    const header = parts[0];
    const base64 = parts[1];
    const mimeMatch = /data:(.*?)(;base64)?$/i.exec(header);
    const mime = mimeType || (mimeMatch ? mimeMatch[1] : 'image/jpeg');
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], fileName, { type: mime });
  } catch (error) {
    console.warn('dataUrlToFile failed', error);
    return null;
  }
}

